
import { Link } from 'react-router-dom';
import { usePublishedPage } from '../hooks/usePublishedPage';
import { useContent } from '../context/ContentContext';
import { GOOGLE_RATING } from '../lib/defaultContent';
import type { LocalServiceContent } from '../types/cmsPages';
import { PhoneIcon, CalendarIcon, CheckCircleIcon, ShieldCheckIcon, ClockIcon, StarIcon } from '@heroicons/react/24/solid';

export default function LocalServicePage() {
  const { settings } = useContent();
  const { page, loading, notFound } = usePublishedPage('local_service');
  if (loading) {
    return (
      <div id="main-content" tabIndex={-1} className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-brand-orange border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound || !page) {
    return (
      <div id="main-content" tabIndex={-1} className="min-h-screen flex flex-col items-center justify-center px-4">
        <h1 className="text-3xl font-serif font-bold text-brand-navy mb-4">Page Not Found</h1>
        <p className="text-gray-500 mb-6">This service page doesn't exist or hasn't been published yet.</p>
        <Link to="/" className="text-brand-orange font-semibold hover:text-brand-navy transition-colors">
          Back to Home
        </Link>
      </div>
    );
  }

  const content = page.content as LocalServiceContent;
  const phone = content.cta_phone || settings.phone;

  return (
    <main id="main-content" tabIndex={-1}>
      <section className="relative bg-brand-navy overflow-hidden">
        {content.hero_image && (
          <div className="absolute inset-0">
            <img decoding="async" loading="lazy" src={content.hero_image} alt="" className="w-full h-full object-cover opacity-20" />
            <div className="absolute inset-0 bg-brand-navy/80" />
          </div>
        )}
        <div className="absolute inset-0">
          <svg className="absolute inset-0 w-full h-full opacity-[0.04]" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="local-grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <circle cx="20" cy="20" r="1" fill="white" />
                <path d="M0 0L40 40M40 0L0 40" stroke="white" strokeWidth="0.5" opacity="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#local-grid)" />
          </svg>
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-gradient-to-b from-brand-orange/8 to-transparent rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 max-w-site mx-auto px-4 sm:px-6 lg:px-8" style={{ paddingTop: '100px', paddingBottom: '100px' }}>
          <nav className="flex items-center gap-2 mb-5 sm:mb-6">
            <Link to="/" className="text-white/50 text-sm hover:text-white/80 transition-colors">Home</Link>
            <svg className="w-3.5 h-3.5 text-white/30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-white/50 text-sm">Services</span>
            <svg className="w-3.5 h-3.5 text-white/30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-brand-orange text-sm font-medium">{content.city}</span>
          </nav>

          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 border border-brand-orange/30 rounded-lg px-4 py-2 mb-6">
                <span className="w-2 h-2 bg-brand-orange rounded-full" />
                <span className="text-brand-orange font-semibold text-sm uppercase tracking-wider">
                  {content.service_type} in {content.city}
                </span>
              </div>

              <h1 className="text-[32px] leading-[1.15] sm:text-[36px] md:text-4xl lg:text-[40px] xl:text-[44px] font-serif font-bold text-white mb-4 sm:mb-6">
                {content.headline || `${content.service_type} Services in ${content.city}`}
              </h1>

              <p className="text-base sm:text-lg text-white/70 leading-relaxed mb-8">
                {content.description}
              </p>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                <a
                  href={settings.bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-brand-orange text-white px-7 py-4 rounded-lg font-semibold hover:bg-white hover:text-brand-navy transition-all duration-300 shadow-lg shadow-brand-orange/25"
                >
                  <CalendarIcon className="w-5 h-5" />
                  {content.cta_text || 'Schedule Service Today'}
                </a>
                <a
                  href={`tel:${phone.replace(/[^0-9]/g, '')}`}
                  className="inline-flex items-center justify-center gap-2 border border-white/20 text-white px-7 py-4 rounded-lg font-semibold hover:border-brand-orange hover:text-brand-orange transition-all duration-300"
                >
                  <PhoneIcon className="w-5 h-5" />
                  {phone}
                </a>
              </div>
            </div>

            <div className="hidden lg:block">
              <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="flex -space-x-1">
                    {[...Array(5)].map((_, i) => (
                      <StarIcon key={i} className="w-5 h-5 text-brand-orange" />
                    ))}
                  </div>
                  <span className="text-white/60 text-sm font-medium">{GOOGLE_RATING} Google Rating</span>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <ShieldCheckIcon className="w-5 h-5 text-brand-orange flex-shrink-0" />
                    <span className="text-white/80 text-sm">Licensed & Insured ({settings.license})</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <ClockIcon className="w-5 h-5 text-brand-orange flex-shrink-0" />
                    <span className="text-white/80 text-sm">Same-Day Appointments Available</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircleIcon className="w-5 h-5 text-brand-orange flex-shrink-0" />
                    <span className="text-white/80 text-sm">Veteran-Owned & Operated</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {content.features && content.features.length > 0 && (
        <section className="py-16 sm:py-20 lg:py-24 bg-brand-off-white">
          <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-bold text-brand-navy mb-4">
                Why Choose Us for {content.service_type} in {content.city}?
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {content.features.map((feature, idx) => (
                <div key={idx} className="bg-white rounded-xl p-6 border border-gray-100 hover:shadow-md transition-shadow">
                  <div className="w-10 h-10 bg-brand-orange/10 rounded-lg flex items-center justify-center mb-4">
                    <CheckCircleIcon className="w-5 h-5 text-brand-orange" />
                  </div>
                  <p className="text-brand-navy font-semibold">{feature}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-16 sm:py-20 lg:py-24 bg-brand-navy">
        <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-bold text-white mb-4">
            Ready for Expert {content.service_type} Service?
          </h2>
          <p className="text-white/70 text-lg mb-8 max-w-2xl mx-auto">
            Serving {content.city} and surrounding areas with reliable, professional HVAC solutions.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={settings.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-brand-orange text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-brand-navy transition-all duration-300 shadow-lg shadow-brand-orange/25"
            >
              <CalendarIcon className="w-5 h-5" />
              Book Your Appointment
            </a>
            <a
              href={`tel:${phone.replace(/[^0-9]/g, '')}`}
              className="inline-flex items-center gap-2 border border-white/20 text-white px-8 py-4 rounded-lg font-semibold hover:border-brand-orange hover:text-brand-orange transition-all duration-300"
            >
              <PhoneIcon className="w-5 h-5" />
              Call {phone}
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
